import * as THREE from "three";

const DEFAULTS = Object.freeze({
    heightMargin: 1.08,
    widthMargin: 1.1,
    eyeLevelOffset: 0,
    // Full posed silhouette by default. A finite cap remains available only
    // for explicitly diagnostic callers; production lesson framing measures
    // every currently deformed vertex.
    maxSamples: Infinity,
});

function finitePositive(value, fallback) {
    return Number.isFinite(value) && value > 0 ? value : fallback;
}

/**
 * Snapshot the visible, currently deformed silhouette in world space.
 * SkinnedMesh.getVertexPosition includes the active skeleton and morph pose;
 * this deliberately does not use world-box corners (which overstate robes and
 * props after perspective projection).
 */
export function collectDeformedWorldVertices(model, maxSamples = DEFAULTS.maxSamples) {
    if (!model) return new Float64Array();
    model.updateMatrixWorld(true);
    const meshes = [];
    let total = 0;
    model.traverseVisible((mesh) => {
        const positions = mesh?.geometry?.attributes?.position;
        if (!positions || !(mesh.isMesh || mesh.isSkinnedMesh)) return;
        meshes.push({ mesh, positions });
        total += positions.count;
    });
    if (!total) return new Float64Array();

    const stride = Math.max(1, Math.ceil(total / finitePositive(maxSamples, DEFAULTS.maxSamples)));
    const values = [];
    const point = new THREE.Vector3();
    let globalIndex = 0;
    for (const { mesh, positions } of meshes) {
        for (let index = 0; index < positions.count; index += 1, globalIndex += 1) {
            if (globalIndex % stride) continue;
            if (typeof mesh.getVertexPosition === "function") {
                mesh.getVertexPosition(index, point);
            } else {
                point.fromBufferAttribute(positions, index);
            }
            mesh.localToWorld(point);
            if (Number.isFinite(point.x) && Number.isFinite(point.y) && Number.isFinite(point.z)) {
                values.push(point.x, point.y, point.z);
            }
        }
    }
    return Float64Array.from(values);
}

function worldExtents(vertices, side) {
    const extents = {
        minY: Infinity,
        maxY: -Infinity,
        minSide: Infinity,
        maxSide: -Infinity,
        minX: Infinity,
        maxX: -Infinity,
        minZ: Infinity,
        maxZ: -Infinity,
    };
    for (let index = 0; index < vertices.length; index += 3) {
        const x = vertices[index];
        const y = vertices[index + 1];
        const z = vertices[index + 2];
        const lateral = x * side.x + z * side.z;
        extents.minY = Math.min(extents.minY, y);
        extents.maxY = Math.max(extents.maxY, y);
        extents.minSide = Math.min(extents.minSide, lateral);
        extents.maxSide = Math.max(extents.maxSide, lateral);
        extents.minX = Math.min(extents.minX, x);
        extents.maxX = Math.max(extents.maxX, x);
        extents.minZ = Math.min(extents.minZ, z);
        extents.maxZ = Math.max(extents.maxZ, z);
    }
    return extents;
}

function projectBounds(camera, vertices) {
    const point = new THREE.Vector3();
    const bounds = {
        left: Infinity,
        right: -Infinity,
        bottom: Infinity,
        top: -Infinity,
        minDepth: Infinity,
        maxDepth: -Infinity,
    };
    for (let index = 0; index < vertices.length; index += 3) {
        point.set(vertices[index], vertices[index + 1], vertices[index + 2]).project(camera);
        bounds.left = Math.min(bounds.left, point.x);
        bounds.right = Math.max(bounds.right, point.x);
        bounds.bottom = Math.min(bounds.bottom, point.y);
        bounds.top = Math.max(bounds.top, point.y);
        bounds.minDepth = Math.min(bounds.minDepth, point.z);
        bounds.maxDepth = Math.max(bounds.maxDepth, point.z);
    }
    return bounds;
}

/**
 * Lesson-only camera solver. The camera stays at the teacher's eye height and
 * looks gently down toward the body, while distance is solved from the actual
 * posed silhouette. Model scale is never changed.
 */
export function solveLessonFraming({
    model,
    camera,
    framing,
    fit = {},
    vertices: suppliedVertices = null,
}) {
    if (!model || !camera) return null;
    const vertices = suppliedVertices || collectDeformedWorldVertices(model, fit.maxSamples);
    if (vertices.length < 3) return null;

    const forwardRaw = framing?.forward || { x: 0, z: 1 };
    const forwardLength = Math.hypot(forwardRaw.x, forwardRaw.z) || 1;
    const forward = new THREE.Vector3(
        forwardRaw.x / forwardLength,
        0,
        forwardRaw.z / forwardLength,
    );
    const side = new THREE.Vector3(forward.z, 0, -forward.x);
    const extents = worldExtents(vertices, side);
    const bodyHeight = extents.maxY - extents.minY;
    if (!(bodyHeight > 0)) return null;

    const lateralCenter = (extents.minSide + extents.maxSide) / 2;
    const worldCenter = new THREE.Vector3(
        (extents.minX + extents.maxX) / 2,
        (extents.minY + extents.maxY) / 2,
        (extents.minZ + extents.maxZ) / 2,
    );
    const currentLateral = worldCenter.x * side.x + worldCenter.z * side.z;
    worldCenter.addScaledVector(side, lateralCenter - currentLateral);

    const inferredEyeY = Number.isFinite(framing?.faceTarget?.y)
        ? framing.faceTarget.y + (Number(framing.faceHeight) || 0) * 0.12
        : extents.maxY - bodyHeight * 0.11;
    const eyeY = inferredEyeY + (Number(fit.eyeLevelOffset) || 0);
    const heightMargin = finitePositive(fit.heightMargin, DEFAULTS.heightMargin);
    const widthMargin = finitePositive(fit.widthMargin, DEFAULTS.widthMargin);
    const verticalLimit = 1 / heightMargin;
    const horizontalLimit = 1 / widthMargin;
    const target = worldCenter.clone();

    const place = (distance) => {
        camera.position.copy(worldCenter).addScaledVector(forward, distance);
        camera.position.y = eyeY;
        // Iteratively center the projected silhouette without lowering the
        // physical camera. This only adjusts the point the eye-level camera
        // looks toward.
        for (let iteration = 0; iteration < 3; iteration += 1) {
            camera.lookAt(target);
            camera.updateMatrixWorld(true);
            const bounds = projectBounds(camera, vertices);
            const centerError = (bounds.top + bounds.bottom) / 2;
            if (Math.abs(centerError) < 0.0005) break;
            const currentPitch = Math.atan2(eyeY - target.y, distance);
            const correction = Math.atan(centerError * Math.tan(THREE.MathUtils.degToRad(camera.fov) / 2));
            target.y = eyeY - Math.tan(currentPitch - correction) * distance;
        }
        camera.lookAt(target);
        camera.updateMatrixWorld(true);
        return projectBounds(camera, vertices);
    };

    const fits = (bounds) =>
        bounds.left >= -horizontalLimit &&
        bounds.right <= horizontalLimit &&
        bounds.bottom >= -verticalLimit &&
        bounds.top <= verticalLimit;

    let low = Math.max(bodyHeight * 0.05, camera.near * 4);
    let high = bodyHeight * 2;
    let bounds = place(high);
    while (!fits(bounds) && high < bodyHeight * 64) {
        high *= 1.6;
        bounds = place(high);
    }
    if (!fits(bounds)) return null;
    for (let iteration = 0; iteration < 24; iteration += 1) {
        const middle = (low + high) / 2;
        const candidate = place(middle);
        if (fits(candidate)) {
            high = middle;
            bounds = candidate;
        } else {
            low = middle;
        }
    }
    bounds = place(high);

    const projectedWidth = bounds.right - bounds.left;
    const projectedHeight = bounds.top - bounds.bottom;
    const cameraDistance = camera.position.distanceTo(target);
    return {
        cameraPosition: camera.position.clone(),
        target: target.clone(),
        distance: cameraDistance,
        horizontalDistance: high,
        eyeY,
        pitchDegrees: THREE.MathUtils.radToDeg(
            Math.atan2(eyeY - target.y, high),
        ),
        bounds,
        occupancy: {
            width: projectedWidth / 2,
            height: projectedHeight / 2,
        },
        margins: { height: heightMargin, width: widthMargin },
        sampledVertices: vertices.length / 3,
        bodyHeight,
    };
}

export function applyLessonFraming(viewer, framing, fit = {}) {
    const result = solveLessonFraming({
        model: viewer?.model,
        camera: viewer?.camera,
        framing,
        fit,
    });
    if (!result || !viewer?.controls) return null;
    viewer.controls.target.copy(result.target);
    viewer.camera.position.copy(result.cameraPosition);
    // OrbitControls clamps spherical radius, not horizontal camera distance.
    // Using the solver's horizontal distance here would pull an elevated
    // camera inward/downward on update and invalidate the measured silhouette.
    viewer.controls.minDistance = result.distance;
    viewer.controls.maxDistance = result.distance;
    viewer.controls.update();
    return result;
}

export default applyLessonFraming;